import { z } from 'zod'
import { AdviesId, Block, DossierId, EventId, InstantieId, PublicatieId, RondeSleutel } from './id.js'
import { Moment, Person, compareMoments } from './moment.js'
import { NormalizedEmail } from './invitation.js'
import { ProcessysteemId, UitvraagAanduiding } from './processysteemRound.js'

/**
 * De berichten aan het processysteem, voor ze XML worden (ontwerp 4.10, 9.4, B9, `koppelvlak.md`
 * hoofdstuk 3).
 *
 * Elke handeling die een revisie sluit schrijft in dezelfde databasetransactie een outbox-rij met
 * het bericht als XML (`messageXml.ts`). Er is geen tweede vorm naast die XML: de schema's hier
 * toetsen alleen wat erin gaat bij een uitnodiging en een opening door beheer, voordat het bericht
 * wordt opgebouwd. Een aparte bezorger levert de outbox at-least-once af, en de ontvanger ontdubbelt
 * op `eventId`.
 *
 * Een handeling in het formulier is een signaal dat de processtap verder zet. Het formulier beheert
 * die stap niet (R-194) en weet niets van toewijzing (R-195). Er komen hier dus geen taken, geen
 * ontvangers en geen toewijzing bij.
 */

/**
 * Welke draaiende vorm het event betreft: de image-digest met het buildnummer.
 *
 * Niet het publicatie-id, want de ontvanger staat buiten het cluster en kan niets
 * opzoeken (9.4). De digest en het buildnummer staan beide op de publicatie, dus
 * dit is een samenstelling van bestaande gegevens en geen nieuw feit.
 */
export const PublicatieRef = z
  .string()
  .regex(
    /^sha256:[0-9a-f]{64}@\S{1,128}$/,
    'verwacht een image-digest met buildnummer, zoals sha256:...@build-42',
  )
export type PublicatieRef = z.infer<typeof PublicatieRef>


/**
 * De link die de ontvanger van een uitnodiging opent: het adres van de publicatie, het pad
 * `/uitnodiging`, en de code in het fragment (R-64c).
 *
 * In het fragment en niet in het pad of de query: een browser stuurt het fragment niet naar de
 * server, dus de code belandt niet in een toegangslog van de ingress of de pod, en niet in de
 * `Referer` van een volgende pagina. De pagina leest haar uit het fragment en biedt haar in het
 * lichaam van een verzoek aan (`POST /uitnodiging`, ontwerp 9.2).
 */
export const InvitationLink = z
  .url({ protocol: /^https$/ })
  .regex(/^https:\/\/[^?#]+\/uitnodiging#[A-Za-z0-9_-]{43}$/, 'verwacht <adres van de publicatie>/uitnodiging#<code>')

/**
 * Het bericht bij een uitnodiging (R-64b, R-64c, ontwerp 9.4, `koppelvlak.md` hoofdstuk 3).
 *
 * Het versturen van de uitnodiging ligt buiten het product (R-64b): dit product maakt haar met
 * haar eenmalige code, en het processysteem brengt de link naar de ontvanger. Het bericht wordt
 * in dezelfde transactie geschreven als de uitnodiging, zodat er geen uitnodiging bestaat
 * waarvan het processysteem het bericht mist.
 *
 * **Een eigen vorm naast het bericht bij een revisie, en geen veld erbij.** Dit bericht sluit geen
 * revisie: er is geen `revisieId`, geen `publicatieRef`, geen `deadline` en geen formulier, en die
 * staan er dan ook niet in, ook niet als null. En `uitgenodigd` is geen
 * waarde van `Event`: dat woord sluit een revisie, en een revisie die sluit met
 * `uitgenodigd` hoort niet te kunnen bestaan.
 */
export const InvitationMessage = z.strictObject({
  eventId: EventId,
  gebeurtenis: z.literal('uitgenodigd'),
  dossierId: DossierId,
  instantieId: InstantieId,
  moment: Moment,
  uitnodiging: z.strictObject({
    /** Het adres waarop is uitgenodigd (R-64), genormaliseerd. */
    email: NormalizedEmail,
    /** De link met de code in het fragment. */
    link: InvitationLink,
    /** Tot wanneer de code te verzilveren is. */
    geldigTot: Moment,
    /** Wie uitnodigde, als ingang met vaste sleutel (R-155e): het processysteem kent de persoon. */
    deler: Person,
    /**
     * Waarvoor is uitgenodigd, alleen bij een uitnodiging om advies (besluit van 30 oktober 2026).
     * Optioneel en additief: een gewone deling noemt het niet. Zo kan het processysteem de mail als
     * adviesvraag formuleren, zonder dat de vraag zelf in de mail komt.
     */
    doel: z.literal('advies').optional(),
    /** Het adviesverzoek bij deze uitnodiging; precies als `doel` er staat. */
    adviesId: AdviesId.optional(),
  }).refine((invitation) => (invitation.doel === undefined) === (invitation.adviesId === undefined), {
    path: ['adviesId'],
    message: 'een uitnodiging om advies noemt haar adviesverzoek, en een gewone deling geen van beide',
  }),
})
export type InvitationMessage = z.infer<typeof InvitationMessage>

/** Een gevulde tekst: na weghalen van witruimte niet leeg. */
const Filled = z.string().trim().min(1)

/**
 * Het bericht bij een opening door functioneel beheer (R-155f, R-155g, ontwerp 9.4,
 * `koppelvlak.md` hoofdstuk 3).
 *
 * Geschreven in dezelfde transactie als de toekenning met bron beheer, zodat er geen opening bestaat
 * waarvan het processysteem het bericht mist; het maakt de opening op het dossier in het
 * processysteem zichtbaar. Een eigen vorm naast het bericht bij een revisie en de uitnodiging, en geen
 * veld erbij, om dezelfde reden als bij de uitnodiging: dit bericht sluit geen revisie, dus de
 * revisievelden staan er niet in, ook niet als null, en `ingezien-door-beheer` is geen waarde van
 * `Event`.
 *
 * De naam van de beheerder en de toelichting gaan mee: het processysteem is intern. De aanvrager
 * ziet alleen de datum en de categorie (R-66b), en dat filtert het overzicht en niet dit bericht.
 * De categorie is hier een gevulde tekst en geen vaste lijst, net als op de toekenning: een
 * ontvanger hoort een latere categorie niet te weigeren.
 */
export const AdminMessage = z
  .strictObject({
    eventId: EventId,
    gebeurtenis: z.literal('ingezien-door-beheer'),
    dossierId: DossierId,
    instantieId: InstantieId,
    moment: Moment,
    beheer: z.strictObject({
      /** Wie keek, als ingang met vaste sleutel (R-155e). */
      persoon: Person,
      redencategorie: Filled,
      /** De interne toelichting op de reden (R-155f). */
      toelichting: Filled,
      meldingsnummer: Filled,
      /** Het einde van de opening. */
      tot: Moment,
    }),
  })
  .refine((message) => compareMoments(message.beheer.tot, message.moment) > 0, {
    path: ['beheer', 'tot'],
    message: 'een opening door beheer eindigt na haar begin',
  })
export type AdminMessage = z.infer<typeof AdminMessage>

/**
 * De melding na publicatie op een plek (besluit 13, R-101, R-203, `koppelvlak.md` hoofdstuk 3).
 *
 * Geschreven in dezelfde transactie als de publicatie, als haar ronde, blok en uitvraagmoment een plek van het
 * processysteem zijn: dan is de plek gevuld en slaagt het aanmaken met haar id. Een publicatie in een ronde met de
 * hand heeft geen plek en geeft geen melding.
 *
 * **Een eigen vorm, zonder dossier en zonder instantie.** Deze melding hoort bij een publicatie en niet bij een
 * dossier: er is nog niets uitgezet. Daarom staan `dossierId` en `instantieId` er niet in, ook niet als null, en
 * evenmin een revisie. `publicatieRef` is de image-digest met het buildnummer, zoals in het bericht bij een revisie;
 * `toetslaag` is wat `heeftToetslaag` van de bevroren definitie zegt. De id's van ronde en plek zijn die van het
 * processysteem (besluit 7), zodat het de melding bij zijn eigen plek kan leggen.
 */
export const PublicationMessage = z.strictObject({
  eventId: EventId,
  gebeurtenis: z.literal('gepubliceerd'),
  moment: Moment,
  publicatieId: PublicatieId,
  publicatieRef: z.string().min(1),
  rondeId: ProcessysteemId,
  plekId: ProcessysteemId,
  ronde: RondeSleutel,
  blok: Block,
  uitvraag: UitvraagAanduiding,
  toetslaag: z.boolean(),
})
export type PublicationMessage = z.infer<typeof PublicationMessage>
